import { Alert, Button, Result, Space, Spin } from 'antd';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createLocalCatalogTransport } from '../../pages/cpq-v2/mock/localCatalogTransport';
import { getLocalDemoCatalog } from '../../pages/cpq-v2/mock/localDemoCatalog';
import { createLocalMediaTransport } from '../../pages/cpq-v2/mock/localMediaTransport';
import { createLocalModuleTransport } from '../../pages/cpq-v2/mock/localModuleTransport';
import {
  beginLoginCompositionLibrary,
  clearLoginCompositionLibrary,
  restoreLoginCompositionLibrary,
} from '../../quote-integration/loginCompositionLibrary';
import type {
  CurrentUser,
  OAuthAuthorization,
  SessionResult,
} from './generated/types';
import {
  createQuoteSessionRecovery,
  type SessionPause,
  sameAuthorization,
} from './quoteSessionRecovery';
import { requestRoleViewSwitch } from './roleViewEditorGuard';
import { restoreInitialSession } from './sessionBootstrap';
import { isVerifiedSessionProjection } from './sessionProjection';
import {
  persistRoleViewSelection,
  type RoleViewOption,
  restoreRoleViewSelection,
  validateRoleView,
} from './sessionRoleView';
import {
  ActiveApiError,
  type ActiveTransport,
  createActiveTransport,
} from './transport';

type RoleViewOptionWithStatus = RoleViewOption & {
  active: boolean;
  authorizationSource?: 'legacy' | 'model3';
};

type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out'; message: string; explicitLogout?: boolean }
  | { status: 'access-denied'; message: string }
  | { status: 'login-required'; message: string }
  | { status: 'login-failed'; message: string }
  | { status: 'unavailable'; message: string }
  | { status: 'ready'; user: CurrentUser; csrfToken: string | null };
type RoleViewBeforeCommit = () =>
  | void
  | (() => void)
  | Promise<undefined | (() => void)>;

/** Published role policies can add or disable role-view choices mid-session. */
export const ROLE_POLICY_PUBLISHED_EVENT = 'cpq-role-policy-published';

type SessionContextValue = {
  state: SessionState;
  offlineDemoEnabled: boolean;
  transport: ActiveTransport;
  quoteTransport: ActiveTransport;
  pause: SessionPause;
  popupLoginError: string | null;
  refresh(): Promise<void>;
  recheckAccess(): Promise<void>;
  logout(): Promise<void>;
  loginWithWeCom(force?: boolean): Promise<void>;
  openWeComLoginWindow(): Promise<void>;
  establishDevelopmentIdentity(identityKey: string): Promise<void>;
  roleViewOptions: RoleViewOption[];
  roleViewSwitching: boolean;
  roleViewError: string | null;
  selectRoleView(
    roleId: string | null,
    beforeCommit?: RoleViewBeforeCommit,
  ): Promise<void>;
};
const SessionContext = createContext<SessionContextValue | null>(null);
export type DevelopmentIdentityFactory = (
  transport: ActiveTransport,
  identityKey: string,
) => Promise<SessionResult>;
export type WeComAuthorizationNavigator = (
  target: string,
  mode: 'assign' | 'replace',
) => void;

const navigateToWeComAuthorization: WeComAuthorizationNavigator = (
  target,
  mode,
) => window.location[mode](target);

const WECOM_LOGIN_POPUP_KEY = 'cpq:wecom-login-popup:v1';
const WECOM_AUTOMATIC_LOGIN_KEY = 'cpq:wecom-automatic-login:v1';
const WECOM_AUTOMATIC_LOGIN_COOLDOWN_MS = 60_000;
const WECOM_LOGIN_AUTHORIZE_TIMEOUT_MS = 10_000;

function automaticWeComLoginBlocked(): boolean {
  try {
    const value = sessionStorage.getItem(WECOM_AUTOMATIC_LOGIN_KEY);
    if (value === null) return false;
    if (!value.startsWith('verified:')) return true;
    const verifiedAt = Number(value.slice('verified:'.length));
    const age = Date.now() - verifiedAt;
    return (
      !Number.isFinite(verifiedAt) ||
      age < 0 ||
      age < WECOM_AUTOMATIC_LOGIN_COOLDOWN_MS
    );
  } catch {
    // Without tab storage an OAuth round trip cannot retain the loop guard.
    return true;
  }
}

function rememberWeComLogin(
  value: 'attempted' | 'logout' | 'verified',
): boolean {
  try {
    sessionStorage.setItem(
      WECOM_AUTOMATIC_LOGIN_KEY,
      value === 'verified' ? `verified:${Date.now()}` : value,
    );
    return true;
  } catch {
    return false;
  }
}

function clearAutomaticWeComLogin(): void {
  try {
    sessionStorage.removeItem(WECOM_AUTOMATIC_LOGIN_KEY);
  } catch {
    // Manual login remains available when tab storage is unavailable.
  }
}

export function isOfflineDemoSessionEnabled({
  dev,
  flag,
  hostname,
}: {
  dev: boolean;
  flag?: string;
  hostname: string;
}): boolean {
  return (
    dev &&
    flag === 'true' &&
    (hostname === 'localhost' || hostname === '127.0.0.1')
  );
}

const offlineDemoUser: CurrentUser = {
  id: 'offline-demo-user',
  companyId: 'offline-demo-company',
  displayName: '本地演练用户',
  status: 'active',
  roles: ['fixed_superadmin'],
  departments: [],
  primaryDepartmentId: 'offline-demo-department',
  permissions: [
    'solution.read',
    'quote.read',
    ...['equipment', 'package', 'plan'].flatMap((module) =>
      ['read', 'create', 'edit', 'set_status', 'delete'].map(
        (action) => `${module}.${action}`,
      ),
    ),
    'customer.read',
    'customer.manage',
    'inventory.read',
    'catalog.replacement.read',
    'catalog.replacement.manage',
    'catalog.replacement.delete',
    'settings.read',
    'settings.manage',
    'quote.container_edit_mode.manage',
    'identity.user.read',
    'identity.user.manage',
    'identity.role.read',
    'identity.role.manage',
    'identity.department.manage',
    'identity.wecom.sync',
    'identity.assignment.read',
    'identity.assignment.manage',
    'identity.user.profile.manage',
    'identity.user.status.manage',
    'identity.session.revoke',
    'identity.role.policy.edit',
    'identity.role.policy.publish',
    'marketing.manage',
  ],
  dataScopes: (
    ['equipment', 'package', 'plan', 'customer', 'quote', 'settings'] as const
  ).map((resourceType) => ({ resourceType, scopeType: 'all' as const })),
  permissionVersion: '1',
};

export function validatedWeComAuthorizationUrl(value: string): string {
  const url = new URL(value);
  const browserQr =
    url.origin === 'https://login.work.weixin.qq.com' &&
    url.pathname === '/wwlogin/sso/login';
  const workbench =
    url.origin === 'https://open.weixin.qq.com' &&
    url.pathname === '/connect/oauth2/authorize' &&
    url.hash === '#wechat_redirect';
  if (!browserQr && !workbench) throw new Error('企业微信授权地址校验失败。');
  return url.toString();
}

async function callbackIdempotencyKey(state: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(state),
  );
  return `wecom-${Array.from(new Uint8Array(digest), (value) =>
    value.toString(16).padStart(2, '0'),
  ).join('')}`;
}

function readWeComCallback(): { code: string; state: string } | null {
  const parameters = new URLSearchParams(window.location.search);
  const code = parameters.get('code');
  const state = parameters.get('state');
  return code && state ? { code, state } : null;
}

function clearWeComCallbackQuery(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState(window.history.state, '', url);
}

function readCsrfToken(): string | null {
  const entry = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith('cpq_csrf='));
  return entry
    ? decodeURIComponent(entry.slice('cpq_csrf='.length)) || null
    : null;
}

export function ActiveSessionProvider({
  children,
  developmentIdentity,
  automaticDevelopmentIdentityKey,
  authorizationNavigator = navigateToWeComAuthorization,
}: {
  children: ReactNode;
  developmentIdentity?: DevelopmentIdentityFactory;
  automaticDevelopmentIdentityKey?: string;
  authorizationNavigator?: WeComAuthorizationNavigator;
}) {
  const offlineDemoFlag = (
    import.meta.env as ImportMetaEnv & {
      readonly VITE_CPQ_OFFLINE_DEMO?: string;
    }
  ).VITE_CPQ_OFFLINE_DEMO;
  const offlineDemoEnabled = isOfflineDemoSessionEnabled({
    dev: import.meta.env.DEV,
    flag: offlineDemoFlag,
    hostname: window.location.hostname,
  });
  const transport = useMemo(() => {
    const base = createActiveTransport(`${window.location.origin}/api/v1`, {
      offlineDemo: offlineDemoEnabled,
    });
    if (!offlineDemoEnabled) return base;
    const snapshot = getLocalDemoCatalog();
    const local = createLocalModuleTransport(createLocalMediaTransport(base), {
      catalog: snapshot ?? undefined,
    });
    return snapshot ? createLocalCatalogTransport(local, snapshot) : local;
  }, [offlineDemoEnabled]);
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const stateRef = useRef(state);
  stateRef.current = state;
  const [recoveryPause, setPause] = useState<SessionPause>(null);
  const pause = offlineDemoEnabled ? null : recoveryPause;
  const [popupLoginError, setPopupLoginError] = useState<string | null>(null);
  const loginPopup = useRef<Window | null>(null);
  const loginPopupPoll = useRef<number | null>(null);
  const loginPopupAttempt = useRef(0);
  const authorizationAttempt = useRef(0);
  const loginPopupExpiresAt = useRef(0);
  const loginPopupRequest = useRef<AbortController | null>(null);
  const [roleViewOptions, setRoleViewOptions] = useState<RoleViewOption[]>([]);
  const [roleViewOptionsRevision, setRoleViewOptionsRevision] = useState(0);
  const [roleViewSwitching, setRoleViewSwitching] = useState(false);
  const [roleViewError, setRoleViewError] = useState<string | null>(null);
  const [roleViewRevision, setRoleViewRevision] = useState(0);
  const roleViewEpoch = useRef(0);
  const recovery = useMemo(() => {
    const epoch = roleViewEpoch.current;
    return createQuoteSessionRecovery({
      transport,
      current: () =>
        epoch === roleViewEpoch.current && stateRef.current.status === 'ready'
          ? {
              ...stateRef.current,
              // Preserve the mounted owner's token so cookie changes trigger
              // identity/authorization revalidation before business dispatch.
              credentialChanged: readCsrfToken() !== stateRef.current.csrfToken,
            }
          : null,
      accept: (session) => {
        if (epoch !== roleViewEpoch.current) return;
        const next: SessionState = {
          status: 'ready',
          user: session.user,
          csrfToken: session.csrfToken,
        };
        stateRef.current = next;
        setState(next);
      },
      pause: (value) => {
        if (epoch === roleViewEpoch.current) {
          setPause(value);
        }
      },
      onSessionEnded: (owner) => clearLoginCompositionLibrary(owner),
      ...(developmentIdentity && automaticDevelopmentIdentityKey
        ? {
            reestablish: async () => {
              const session = await developmentIdentity(
                transport,
                automaticDevelopmentIdentityKey,
              );
              beginLoginCompositionLibrary(session.user);
              return session;
            },
          }
        : {}),
    });
  }, [
    automaticDevelopmentIdentityKey,
    developmentIdentity,
    transport,
    roleViewRevision,
  ]);
  useEffect(() => {
    if (pause?.kind !== 'connection') return;
    const retry = () => {
      void recovery.refresh().catch(() => undefined);
    };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [pause, recovery]);
  useEffect(() => {
    return () => {
      authorizationAttempt.current += 1;
      loginPopupAttempt.current += 1;
      loginPopupRequest.current?.abort();
      if (loginPopupPoll.current !== null)
        window.clearInterval(loginPopupPoll.current);
    };
  }, []);
  const stopLoginPopupPoll = useCallback(() => {
    if (loginPopupPoll.current !== null) {
      window.clearInterval(loginPopupPoll.current);
      loginPopupPoll.current = null;
    }
  }, []);
  const openWeComLoginWindow = useCallback(async () => {
    if (
      loginPopup.current &&
      !loginPopup.current.closed &&
      Date.now() < loginPopupExpiresAt.current
    ) {
      loginPopup.current.focus();
      return;
    }
    stopLoginPopupPoll();
    const attempt = ++loginPopupAttempt.current;
    loginPopupRequest.current?.abort();
    loginPopup.current?.close();
    loginPopup.current = null;
    loginPopupExpiresAt.current = Date.now() + WECOM_LOGIN_AUTHORIZE_TIMEOUT_MS;
    setPopupLoginError(null);
    // Opening must happen in the click handler, before the async challenge.
    // Otherwise browsers can block the official QR login page.
    let popup: Window | null;
    try {
      popup = window.open('about:blank', '_blank');
    } catch {
      popup = null;
    }
    if (!popup) {
      setPopupLoginError('浏览器阻止了扫码窗口，请允许弹出窗口后重试。');
      return;
    }
    try {
      popup.sessionStorage.setItem(WECOM_LOGIN_POPUP_KEY, String(Date.now()));
      popup.opener = null;
      popup.document.title = '企业微信扫码登录';
      popup.document.body.textContent = '正在打开企业微信扫码页面…';
      loginPopup.current = popup;
      const controller = new AbortController();
      loginPopupRequest.current = controller;
      let timeout: number | undefined;
      const authorization = (async (): Promise<OAuthAuthorization> => {
        const response = await transport.requestResponse(
          '/auth/wecom/authorize',
          {
            signal: controller.signal,
          },
        );
        if (!response.ok)
          throw new ActiveApiError(
            response.status,
            '企业微信扫码启动失败，请重试。',
          );
        return (await response.json()) as OAuthAuthorization;
      })();
      let challenge: OAuthAuthorization;
      try {
        challenge = await Promise.race([
          authorization,
          new Promise<never>((_resolve, reject) => {
            timeout = window.setTimeout(() => {
              reject(
                new ActiveApiError(
                  null,
                  '企业微信扫码启动超时，请重新打开扫码页。',
                ),
              );
              controller.abort();
            }, WECOM_LOGIN_AUTHORIZE_TIMEOUT_MS);
          }),
        ]);
      } finally {
        window.clearTimeout(timeout);
        if (loginPopupRequest.current === controller)
          loginPopupRequest.current = null;
      }
      if (attempt !== loginPopupAttempt.current) {
        popup.close();
        return;
      }
      const expiresAt = Date.parse(challenge.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now())
        throw new Error('企业微信扫码已过期，请重新打开扫码页。');
      loginPopupExpiresAt.current = expiresAt;
      const target = validatedWeComAuthorizationUrl(challenge.authorizationUrl);
      if (popup.closed) return;
      popup.location.replace(target);
      let checking = false;
      const originalCsrf = readCsrfToken();
      let pendingCsrf: string | null = null;
      let attempts = 0;
      let retryAfter = 0;
      const poll = window.setInterval(() => {
        if (attempt !== loginPopupAttempt.current) {
          window.clearInterval(poll);
          return;
        }
        const currentCsrf = readCsrfToken();
        if (currentCsrf === originalCsrf) {
          if (!checking && (popup.closed || Date.now() >= expiresAt)) {
            stopLoginPopupPoll();
            popup.close();
            loginPopup.current = null;
            if (Date.now() >= expiresAt)
              setPopupLoginError('企业微信扫码已过期，请重新打开扫码页。');
          }
          return;
        }
        if (currentCsrf !== pendingCsrf) {
          pendingCsrf = currentCsrf;
          attempts = 0;
          retryAfter = 0;
        }
        if (checking || Date.now() < retryAfter) return;
        checking = true;
        attempts += 1;
        void recovery
          .recheckAfterExternalLogin()
          .then(
            () => {
              if (attempt !== loginPopupAttempt.current) return;
              stopLoginPopupPoll();
              popup.close();
              loginPopup.current = null;
              setPopupLoginError(null);
            },
            (error: unknown) => {
              if (attempt !== loginPopupAttempt.current) return;
              const retryable =
                !(error instanceof ActiveApiError) ||
                error.status === null ||
                error.status >= 500;
              if (retryable && attempts < 5) {
                retryAfter =
                  Date.now() + Math.min(8_000, 1_000 * 2 ** (attempts - 1));
                setPopupLoginError('扫码已完成，正在重新核验连接…');
              } else {
                stopLoginPopupPoll();
                popup.close();
                loginPopup.current = null;
                setPopupLoginError(
                  error instanceof Error
                    ? error.message
                    : '扫码后会话核验失败，请重新检查。',
                );
              }
            },
          )
          .finally(() => {
            checking = false;
          });
      }, 1_000);
      loginPopupPoll.current = poll;
    } catch (error) {
      if (attempt !== loginPopupAttempt.current) return;
      stopLoginPopupPoll();
      popup.close();
      loginPopup.current = null;
      setPopupLoginError(
        error instanceof Error ? error.message : '企业微信扫码启动失败。',
      );
    }
  }, [recovery, stopLoginPopupPoll, transport]);
  const recoverFlight = useRef<Promise<void> | null>(null);
  const recover = useCallback((): Promise<void> => {
    if (recoverFlight.current) return recoverFlight.current;
    const operation = async () => {
      setState({ status: 'loading' });
      if (offlineDemoEnabled) {
        setPause(null);
        restoreLoginCompositionLibrary(offlineDemoUser);
        setState({
          status: 'ready',
          user: offlineDemoUser,
          csrfToken: 'local-demo-only',
        });
        return;
      }
      try {
        const callback = readWeComCallback();
        if (callback) {
          try {
            const session = await transport.completeWeComAuthorization(
              callback,
              await callbackIdempotencyKey(callback.state),
            );
            if ('user' in session) {
              // A new login must not inherit an earlier member's role selection.
              transport.roleViewDispatch.select(null);
              persistRoleViewSelection(session.user, null);
              const beforeCsrf = readCsrfToken();
              if (beforeCsrf !== null && beforeCsrf !== session.csrfToken)
                throw new ActiveApiError(
                  null,
                  '登录正在由其他页面更新，请重试连接。',
                  'SESSION_CHANGED_DURING_REVALIDATION',
                );
              const user = await transport.getCurrentUser('original');
              if (readCsrfToken() !== beforeCsrf)
                throw new ActiveApiError(
                  null,
                  '登录正在由其他页面更新，请重试连接。',
                  'SESSION_CHANGED_DURING_REVALIDATION',
                );
              if (!isVerifiedSessionProjection(session.user, user, null))
                throw new ActiveApiError(
                  401,
                  '登录期间身份已变化，请重新检查。',
                  'SESSION_IDENTITY_CHANGED',
                );
              beginLoginCompositionLibrary(user);
              setState({ status: 'ready', user, csrfToken: session.csrfToken });
              return;
            }
            setState({
              status: 'access-denied',
              message:
                '企业微信身份已验证，账号尚未获得应用授权。请联系管理员配置权限后重新检查。',
            });
            return;
          } catch (error) {
            if (
              error instanceof ActiveApiError &&
              (error.status === 400 || error.status === 401)
            ) {
              // A rejected OAuth callback needs a new challenge, not a replay
              // of the consumed code or an automatic authorization loop.
              setState({
                status: 'login-failed',
                message: '本次企业微信登录未完成，请重新扫码登录。',
              });
              return;
            }
            throw error;
          } finally {
            clearWeComCallbackQuery();
          }
        }
        const restored = await restoreInitialSession({
          transport,
          csrf: readCsrfToken,
          ...(automaticDevelopmentIdentityKey && developmentIdentity
            ? {
                developmentLogin: () =>
                  developmentIdentity(
                    transport,
                    automaticDevelopmentIdentityKey,
                  ),
              }
            : {}),
        });
        const user = await restoreRoleViewSelection(transport, restored.user);
        restoreLoginCompositionLibrary(user);
        setState({ status: 'ready', ...restored, user });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : '会话恢复失败。';
        if (error instanceof ActiveApiError && error.status === 401)
          clearLoginCompositionLibrary(
            state.status === 'ready' ? state.user : undefined,
          );
        if (error instanceof ActiveApiError && error.status === 403) {
          setState({
            status: 'access-denied',
            message:
              error.code === 'USER_PENDING_AUTHORIZATION'
                ? '当前账号尚未完成角色授权，或原有角色需要核对。请联系管理员确认后重新检查。'
                : '当前账号暂无系统访问权限。请联系管理员配置权限后重新检查。',
          });
          return;
        }
        if (
          error instanceof ActiveApiError &&
          error.status === 401 &&
          (error.code === 'SESSION_REVOKED' ||
            error.code === 'SESSION_PERMISSION_VERSION_STALE')
        ) {
          setState({
            status: 'login-required',
            message: '当前登录会话已失效或权限已变化，请重新登录。',
          });
          return;
        }
        setState(
          error instanceof ActiveApiError && error.status === 401
            ? { status: 'signed-out', message }
            : { status: 'unavailable', message },
        );
      }
    };
    recoverFlight.current = operation().finally(() => {
      recoverFlight.current = null;
    });
    return recoverFlight.current;
  }, [
    offlineDemoEnabled,
    transport,
    automaticDevelopmentIdentityKey,
    developmentIdentity,
  ]);
  useEffect(() => {
    void recover();
  }, []);
  const roleViewOwner =
    state.status === 'ready' && state.user.roleViewEnabled === true
      ? `${state.user.companyId}:${state.user.id}`
      : null;
  useEffect(() => {
    let active = true;
    if (!roleViewOwner) {
      setRoleViewOptions([]);
      return;
    }
    void recovery.transport
      .requestResponse('/roles', { roleView: 'original' })
      .then(async (response) => {
        if (!response.ok)
          throw new ActiveApiError(response.status, '角色列表读取失败');
        return (await response.json()) as { items: RoleViewOptionWithStatus[] };
      })
      .then(({ items }) => {
        if (active) {
          setRoleViewOptions(
            items
              // /me validates the selected published policy and administrator
              // authority before the session commits either authorization model.
              .filter((role) => role.active)
              .map(({ id, key, name, authorizationSource }) => ({
                id,
                key,
                name,
                ...(authorizationSource ? { authorizationSource } : {}),
              })),
          );
          setRoleViewError(null);
        }
      })
      .catch((error) => {
        if (active)
          setRoleViewError(
            error instanceof Error ? error.message : '角色列表读取失败',
          );
      });
    return () => {
      active = false;
    };
  }, [roleViewOwner, recovery.transport, roleViewOptionsRevision]);
  useEffect(() => {
    const refreshOptions = () =>
      setRoleViewOptionsRevision((revision) => revision + 1);
    window.addEventListener(ROLE_POLICY_PUBLISHED_EVENT, refreshOptions);
    return () =>
      window.removeEventListener(ROLE_POLICY_PUBLISHED_EVENT, refreshOptions);
  }, []);
  // Keep business runtime clients stable while a target /me request is pending.
  // Only a real session/recovery owner change may rebuild their authorization.
  const refresh = useCallback(async () => {
    if (stateRef.current.status === 'ready') {
      if (offlineDemoEnabled) return;
      await recovery.refresh().catch(() => undefined);
      return;
    }
    if (stateRef.current.status === 'unavailable') clearAutomaticWeComLogin();
    await recover();
  }, [offlineDemoEnabled, recovery, recover]);
  const value = useMemo(
    () => ({
      state,
      offlineDemoEnabled,
      // 正式请求共用续期协调器；离线演示只使用禁止网络访问的通道。
      transport: offlineDemoEnabled ? transport : recovery.transport,
      quoteTransport: offlineDemoEnabled ? transport : recovery.transport,
      pause,
      popupLoginError,
      roleViewOptions,
      roleViewSwitching,
      roleViewError,
      selectRoleView: async (
        roleId: string | null,
        beforeCommit?: RoleViewBeforeCommit,
      ) => {
        const before = stateRef.current;
        if (
          before.status !== 'ready' ||
          before.user.roleViewEnabled !== true ||
          roleViewSwitching ||
          (pause && roleId !== null)
        )
          return;
        if (!requestRoleViewSwitch().allowed)
          throw new Error('请先保存或关闭目录编辑窗口后再切换角色');
        if (roleId && !roleViewOptions.some((role) => role.id === roleId))
          throw new Error('验收角色不存在或未启用');
        setRoleViewSwitching(true);
        transport.roleViewDispatch.setSwitching(true);
        try {
          // A role-view read uses the same renewal owner as business requests.
          // It must not strand a valid session at the access-token TTL boundary.
          let user: CurrentUser;
          if (pause?.kind === 'authorization' && roleId === null) {
            // The blocked target policy cannot validate its own recovery.
            // Explicit exit still requires a fresh original-authority /me;
            // no cached or refresh-response administrator is accepted.
            user = await transport.getCurrentUser('original');
          } else {
            const response = await recovery.transport.requestResponse('/me', {
              roleView: roleId ?? 'original',
            });
            if (!response.ok) {
              const problem = await response.json().catch(() => ({}));
              throw new ActiveApiError(
                response.status,
                `后端请求失败（HTTP ${response.status}）。`,
                typeof problem?.code === 'string' ? problem.code : undefined,
              );
            }
            user = (await response.json()) as CurrentUser;
          }
          validateRoleView(before.user, user, roleId);
          if (!requestRoleViewSwitch().allowed)
            throw new Error(
              '目录编辑窗口已打开，未切换角色；请先保存或关闭当前编辑',
            );
          const beforeRecovery = stateRef.current;
          if (
            beforeRecovery.status !== 'ready' ||
            !sameAuthorization(before.user, beforeRecovery.user)
          )
            throw new Error('登录状态已变化，未切换验收角色');
          // The transaction must finish before committing the new role. A final
          // synchronous checkpoint check protects edits made during either await.
          const verifyRecoveryCheckpoint = await beforeCommit?.();
          const current = stateRef.current;
          if (
            current.status !== 'ready' ||
            !sameAuthorization(before.user, current.user)
          )
            throw new Error('登录状态已变化，未切换验收角色');
          if (!requestRoleViewSwitch().allowed)
            throw new Error(
              '目录编辑窗口已打开，未切换角色；请先保存或关闭当前编辑',
            );
          verifyRecoveryCheckpoint?.();
          roleViewEpoch.current += 1;
          transport.roleViewDispatch.select(roleId);
          persistRoleViewSelection(user, roleId);
          const next: SessionState = { ...current, user };
          stateRef.current = next;
          setState(next);
          setRoleViewRevision((revision) => revision + 1);
          setPause(null);
          setRoleViewError(null);
        } catch (error) {
          setRoleViewError(
            error instanceof Error ? error.message : '角色切换失败',
          );
          throw error;
        } finally {
          transport.roleViewDispatch.setSwitching(false);
          setRoleViewSwitching(false);
        }
      },
      refresh,
      recheckAccess: recover,
      logout: async () => {
        if (pause) return;
        if (offlineDemoEnabled) {
          rememberWeComLogin('logout');
          clearLoginCompositionLibrary(offlineDemoUser);
          setState({
            status: 'signed-out',
            message: '已退出本地演练。',
            explicitLogout: true,
          });
          return;
        }
        try {
          if (state.status !== 'ready') return;
          const csrfToken = state.csrfToken ?? readCsrfToken();
          if (!csrfToken)
            throw new ActiveApiError(403, '缺少 CSRF cookie，无法退出登录。');
          await transport.logoutSession(csrfToken);
          clearLoginCompositionLibrary(state.user);
          transport.roleViewDispatch.select(null);
          persistRoleViewSelection(state.user, null);
          rememberWeComLogin('logout');
          setState({
            status: 'signed-out',
            message: '已退出登录。',
            explicitLogout: true,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : '退出登录失败。';
          if (error instanceof ActiveApiError && error.status === 401)
            clearLoginCompositionLibrary(
              state.status === 'ready' ? state.user : undefined,
            );
          if (error instanceof ActiveApiError && error.status === 401)
            rememberWeComLogin('logout');
          setState(
            error instanceof ActiveApiError && error.status === 401
              ? {
                  status: 'signed-out',
                  message,
                  explicitLogout: true,
                }
              : { status: 'unavailable', message },
          );
        }
      },
      loginWithWeCom: async (force = false) => {
        if (pause && !force) return;
        if (offlineDemoEnabled) {
          await recover();
          return;
        }
        if (force) setPause(null);
        const attempt = ++authorizationAttempt.current;
        try {
          rememberWeComLogin('attempted');
          setState({ status: 'loading' });
          const challenge = await transport.beginWeComAuthorization();
          if (attempt !== authorizationAttempt.current) return;
          const target = validatedWeComAuthorizationUrl(
            challenge.authorizationUrl,
          );
          const url = new URL(target);
          authorizationNavigator(
            target,
            url.hostname === 'open.weixin.qq.com' ? 'replace' : 'assign',
          );
        } catch (error) {
          if (attempt !== authorizationAttempt.current) return;
          setState({
            status: 'unavailable',
            message:
              error instanceof Error ? error.message : '企业微信授权启动失败。',
          });
        }
      },
      openWeComLoginWindow,
      establishDevelopmentIdentity: async (identityKey: string) => {
        if (pause) return;
        if (offlineDemoEnabled) {
          await recover();
          return;
        }
        try {
          if (!developmentIdentity) return;
          const session = await developmentIdentity(transport, identityKey);
          const user = await transport.getCurrentUser();
          beginLoginCompositionLibrary(user);
          setState({ status: 'ready', user, csrfToken: session.csrfToken });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : '固定身份登录失败。';
          setState({ status: 'unavailable', message });
        }
      },
    }),
    [
      authorizationNavigator,
      developmentIdentity,
      offlineDemoEnabled,
      state,
      transport,
      recovery,
      refresh,
      recover,
      pause,
      popupLoginError,
      openWeComLoginWindow,
      roleViewOptions,
      roleViewSwitching,
      roleViewError,
    ],
  );
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useActiveSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('ActiveSessionProvider is required');
  return value;
}

export function useOptionalActiveSession() {
  return useContext(SessionContext);
}

export function ActiveSessionShell({
  developmentIdentityEnabled = false,
  developmentIdentityKey,
  wecomLoginEnabled = false,
  beforeReauthentication,
  children,
}: {
  developmentIdentityEnabled?: boolean;
  developmentIdentityKey?: string;
  wecomLoginEnabled?: boolean;
  /** Verify the existing scoped draft checkpoint before leaving a mounted editor. */
  beforeReauthentication?: () => Promise<boolean>;
  children?: ReactNode;
}) {
  const {
    state,
    pause,
    popupLoginError,
    refresh,
    recheckAccess,
    logout,
    loginWithWeCom,
    openWeComLoginWindow,
    establishDevelopmentIdentity,
    offlineDemoEnabled,
    selectRoleView,
  } = useActiveSession();
  const automaticLoginStarted = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [automaticLoginBlocked, setAutomaticLoginBlocked] = useState(
    automaticWeComLoginBlocked,
  );
  const [automaticLoginError, setAutomaticLoginError] = useState<string | null>(
    null,
  );
  const currentLoginContext = useRef({ state, pause });
  currentLoginContext.current = { state, pause };
  const explicitLogout = state.status === 'signed-out' && state.explicitLogout;
  useEffect(() => {
    if (state.status === 'ready' && !pause) {
      try {
        const previous = sessionStorage.getItem(WECOM_AUTOMATIC_LOGIN_KEY);
        if (previous === 'attempted' || previous === 'logout')
          rememberWeComLogin('verified');
      } catch {
        // A missing durable loop guard disables automatic navigation.
      }
      automaticLoginStarted.current = false;
      setAutomaticLoginBlocked(automaticWeComLoginBlocked());
      setAutomaticLoginError(null);
      return;
    }
    if (explicitLogout) {
      rememberWeComLogin('logout');
      setAutomaticLoginBlocked(true);
      return;
    }
    if (state.status === 'unavailable') automaticLoginStarted.current = false;
    const expiredEditor =
      state.status === 'ready' && pause?.kind === 'authentication';
    if (
      wecomLoginEnabled &&
      (state.status === 'signed-out' ||
        state.status === 'login-required' ||
        expiredEditor) &&
      !explicitLogout &&
      !automaticLoginStarted.current &&
      !automaticWeComLoginBlocked() &&
      (!expiredEditor || beforeReauthentication)
    ) {
      automaticLoginStarted.current = true;
      setAutomaticLoginBlocked(true);
      if (!rememberWeComLogin('attempted')) return;
      void (async () => {
        if (expiredEditor) {
          let preserved = false;
          try {
            preserved = (await beforeReauthentication?.()) ?? false;
          } catch {
            // Keep the inert editor and its manual popup recovery available.
          }
          if (
            !mounted.current ||
            currentLoginContext.current.state !== state ||
            currentLoginContext.current.pause !== pause
          )
            return;
          if (!preserved) {
            setAutomaticLoginError(
              '暂时无法安全离开当前编辑，请使用扫码窗口重新登录。',
            );
            return;
          }
        }
        await loginWithWeCom(expiredEditor);
      })();
    }
  }, [
    loginWithWeCom,
    state,
    pause,
    explicitLogout,
    wecomLoginEnabled,
    beforeReauthentication,
  ]);
  if (state.status === 'loading')
    return <Result icon={<Spin />} title="正在恢复登录会话" />;
  if (
    state.status === 'access-denied' ||
    (state.status === 'ready' &&
      state.user.permissions.length === 0 &&
      state.user.roleViewEnabled !== true)
  )
    return (
      <Result
        status="403"
        title="暂无系统访问权限"
        subTitle={
          state.status === 'access-denied'
            ? state.message
            : '当前账号已登录，但尚未获得可访问的功能权限。请联系管理员配置权限后重新检查。'
        }
        extra={
          <Space>
            <Button type="primary" onClick={() => void recheckAccess()}>
              重新检查权限
            </Button>
            {state.status === 'ready' ? (
              <Button onClick={() => void logout()}>退出登录</Button>
            ) : null}
          </Space>
        }
      />
    );
  if (state.status === 'ready')
    return (
      <>
        {pause?.kind === 'connection' ? (
          <Alert
            type="warning"
            showIcon
            title="连接暂时异常"
            description={
              <>
                <div>{pause.message}</div>
                {popupLoginError ? (
                  <div role="alert">{popupLoginError}</div>
                ) : null}
              </>
            }
            action={
              <Space>
                <Button onClick={() => void refresh()}>重试连接</Button>
                {wecomLoginEnabled ? (
                  <Button onClick={() => void openWeComLoginWindow()}>
                    打开企业微信扫码页
                  </Button>
                ) : null}
              </Space>
            }
          />
        ) : null}
        <SessionPauseDialog
          pause={pause}
          popupLoginError={popupLoginError ?? automaticLoginError}
          exitRoleView={
            state.user.roleView
              ? () => void selectRoleView(null).catch(() => undefined)
              : undefined
          }
          retry={() => void refresh()}
          login={
            wecomLoginEnabled ? () => void openWeComLoginWindow() : undefined
          }
        />
        {children ?? (
          <Result
            status="success"
            title={`已恢复会话：${state.user.displayName}`}
          />
        )}
      </>
    );
  const identityKey = new URLSearchParams(window.location.search).get(
    'identity',
  );
  const fixedIdentityKey =
    developmentIdentityEnabled && !offlineDemoEnabled
      ? (identityKey ?? developmentIdentityKey)
      : null;
  return (
    <Result
      status={state.status === 'signed-out' ? 'info' : 'warning'}
      title={
        state.status === 'login-failed'
          ? '企业微信登录未完成'
          : state.status === 'login-required'
            ? '需要重新登录'
            : state.status === 'unavailable'
              ? '后端不可用'
              : '未登录'
      }
      subTitle={state.message}
      extra={
        <Space>
          {wecomLoginEnabled &&
          (state.status === 'login-failed' ||
            state.status === 'login-required' ||
            state.status === 'unavailable' ||
            (state.status === 'signed-out' &&
              (state.explicitLogout || automaticLoginBlocked))) ? (
            <Button type="primary" onClick={() => void loginWithWeCom()}>
              重新进入企业微信登录
            </Button>
          ) : null}
          {fixedIdentityKey && developmentIdentityEnabled ? (
            <Button
              type="primary"
              onClick={() =>
                void establishDevelopmentIdentity(fixedIdentityKey)
              }
            >
              本地开发登录
            </Button>
          ) : null}
          <Button onClick={() => void refresh()}>重新检查</Button>
        </Space>
      }
    />
  );
}

/** Native modal top layer also covers body portals. The existing subtree stays
 * mounted, but is inert while authentication is unresolved. No second app tree.
 */
function SessionPauseDialog({
  pause,
  popupLoginError,
  retry,
  login,
  exitRoleView,
}: {
  pause: SessionPause;
  popupLoginError: string | null;
  retry(): void;
  login?: () => void;
  exitRoleView?: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    if (pause && pause.kind !== 'connection' && !dialog.current?.open)
      dialog.current?.showModal();
    if ((!pause || pause.kind === 'connection') && dialog.current?.open)
      dialog.current?.close();
  }, [pause]);
  return (
    <dialog
      ref={dialog}
      aria-label="会话恢复"
      onCancel={(event) => event.preventDefault()}
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        border: 0,
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        maxHeight: 'none',
        background: '#fff',
      }}
    >
      {pause && pause.kind !== 'connection' ? (
        <Result
          status="warning"
          title={
            pause.kind === 'authorization'
              ? '访问权限已变化'
              : pause.kind === 'eligibility'
                ? '企业微信使用资格已失效'
                : '需要重新登录'
          }
          subTitle={pause.message}
          extra={
            <Space>
              {exitRoleView ? (
                <Button onClick={exitRoleView}>退出角色验收</Button>
              ) : null}
              {pause.retryable ? (
                <Button onClick={retry}>重试会话续期</Button>
              ) : null}
              {login && pause.kind === 'authentication' ? (
                <Button type="primary" onClick={login}>
                  打开企业微信扫码页
                </Button>
              ) : null}
              {popupLoginError ? (
                <span role="alert">{popupLoginError}</span>
              ) : null}
            </Space>
          }
        />
      ) : null}
    </dialog>
  );
}
